// ============================================================
// Chess Arena — Player State (Singleton)
// ============================================================
//
// Shared player state imported by both HTML screens and Phaser
// scenes. Persists via SaveManager. Trophy brackets, arena
// configs, and XP levels use hardcoded GDD defaults until
// GameConfig loads async configs.
// ============================================================

import {
  PlayerStateData,
  ChestSlot,
  ActiveTask,
  ArenaConfig,
  TrophyBracket,
  XPLevelConfig,
} from './types';
import saveManager from '../utils/saveManager';

// --- GDD Hardcoded Defaults ---

const DEFAULT_TROPHY_BRACKETS: TrophyBracket[] = [
  { minTrophies: 0,    maxTrophies: 99,   winGain: 30, lossLoss: 0  },
  { minTrophies: 100,  maxTrophies: 199,  winGain: 25, lossLoss: 2  },
  { minTrophies: 200,  maxTrophies: 399,  winGain: 20, lossLoss: 5  },
  { minTrophies: 400,  maxTrophies: 599,  winGain: 15, lossLoss: 8  },
  { minTrophies: 600,  maxTrophies: 799,  winGain: 12, lossLoss: 8  },
  { minTrophies: 800,  maxTrophies: 1199, winGain: 10, lossLoss: 8  },
  { minTrophies: 1200, maxTrophies: 1799, winGain: 10, lossLoss: 10 },
  { minTrophies: 1800, maxTrophies: 99999, winGain: 8, lossLoss: 10 },
];

const DEFAULT_ARENAS: ArenaConfig[] = [
  { id: 1, trophies: 0,    name: "Beginner's Square",     boardTheme: 'stone_courtyard',   unlockRewardCoins: 0    },
  { id: 2, trophies: 100,  name: 'Wooden Tavern',         boardTheme: 'warm_wood',         unlockRewardCoins: 100  },
  { id: 3, trophies: 250,  name: "Knight's Hall",         boardTheme: 'castle_interior',   unlockRewardCoins: 200  },
  { id: 4, trophies: 500,  name: "Bishop's Cathedral",    boardTheme: 'marble_stained',    unlockRewardCoins: 300  },
  { id: 5, trophies: 800,  name: "Rook's Fortress",       boardTheme: 'dark_stone',        unlockRewardCoins: 500  },
  { id: 6, trophies: 1200, name: 'Royal Court',           boardTheme: 'royal_purple',      unlockRewardCoins: 500  },
  { id: 7, trophies: 1800, name: "Grandmaster's Tower",   boardTheme: 'crystal_ice',       unlockRewardCoins: 1000 },
  { id: 8, trophies: 2500, name: "Champion's Arena",      boardTheme: 'lava_obsidian',     unlockRewardCoins: 1000 },
];

const DEFAULT_XP_LEVELS: XPLevelConfig[] = [
  { level: 1,  xpRequired: 100  },
  { level: 2,  xpRequired: 200  },
  { level: 3,  xpRequired: 350  },
  { level: 4,  xpRequired: 500  },
  { level: 5,  xpRequired: 700  },
  { level: 6,  xpRequired: 1000 },
  { level: 7,  xpRequired: 1400 },
  { level: 8,  xpRequired: 1800 },
  { level: 9,  xpRequired: 2300 },
  { level: 10, xpRequired: 3000 },
];

const MAX_LIVES = 5;
const LIFE_REGEN_MS = 20 * 60 * 1000; // 20 minutes
const WIN_STREAK_BONUS = 3;
const WIN_STREAK_MIN_WINS = 2;
const WIN_STREAK_BONUS_CAP = 15;
const COINS_PER_WIN = 50;
const XP_PER_WIN = 100;
const XP_PER_LOSS = 30;

function createDefaultState(): PlayerStateData {
  const emptySlots: ChestSlot[] = Array.from({ length: 4 }, () => ({
    state: 'empty' as const,
    tier: null,
    unlockEndTime: null,
  }));

  return {
    currentLevel: 1,
    highestLevelCompleted: 0,
    trophies: 0,
    coins: 0,
    xp: 0,
    xpLevel: 1,
    lives: 5,
    maxLives: MAX_LIVES,
    lastLifeRegenTimestamp: Date.now(),
    winStreak: 0,
    chestSlots: emptySlots,
    overflowChest: null,
    dailyTasks: [],
    dailyTasksLastReset: '',
    dailyChestClaimed: false,
    bronzeStreak: 0,
    chestsSinceGold: 0,
    abilitiesUnlocked: [],
    abilityShards: {},
    abilityCharges: {},
    equippedAbilities: [],
    onboardingComplete: false,
    mainMenuTutorialDone: false,
    saveVersion: 3,
  };
}

class PlayerState {
  private data: PlayerStateData;

  constructor() {
    this.data = createDefaultState();
    this.load();
  }

  // --- Getters ---

  get currentLevel(): number {
    return this.data.currentLevel;
  }

  get highestLevelCompleted(): number {
    return this.data.highestLevelCompleted;
  }

  get trophies(): number {
    return this.data.trophies;
  }

  get coins(): number {
    return this.data.coins;
  }

  get xp(): number {
    return this.data.xp;
  }

  get xpLevel(): number {
    return this.data.xpLevel;
  }

  get lives(): number {
    return this.data.lives;
  }

  get maxLives(): number {
    return this.data.maxLives;
  }

  get winStreak(): number {
    return this.data.winStreak;
  }

  get chestSlots(): ChestSlot[] {
    return this.data.chestSlots;
  }

  get overflowChest(): ChestSlot | null {
    return this.data.overflowChest;
  }

  get dailyTasks(): ActiveTask[] {
    return this.data.dailyTasks;
  }

  get dailyTasksLastReset(): string {
    return this.data.dailyTasksLastReset;
  }

  get dailyChestClaimed(): boolean {
    return this.data.dailyChestClaimed;
  }

  get bronzeStreak(): number {
    return this.data.bronzeStreak;
  }

  get chestsSinceGold(): number {
    return this.data.chestsSinceGold;
  }

  get abilitiesUnlocked(): string[] {
    return this.data.abilitiesUnlocked;
  }

  get abilityShards(): Record<string, number> {
    return this.data.abilityShards;
  }

  get abilityCharges(): Record<string, number> {
    return this.data.abilityCharges;
  }

  get equippedAbilities(): string[] {
    return this.data.equippedAbilities;
  }

  get onboardingComplete(): boolean {
    return this.data.onboardingComplete;
  }

  get mainMenuTutorialDone(): boolean {
    return this.data.mainMenuTutorialDone;
  }

  setMainMenuTutorialDone(done: boolean): void {
    this.data.mainMenuTutorialDone = done;
    this.save();
  }

  /** Computed: current arena ID based on trophy count. */
  get arena(): number {
    return this.getCurrentArena().id;
  }

  // --- Direct data access (for systems that need to mutate sub-objects) ---

  getRawData(): PlayerStateData {
    return this.data;
  }

  // --- Mutators ---

  addCoins(amount: number): void {
    this.data.coins += amount;
    this.save();
  }

  /**
   * Add XP. Returns true if the player leveled up.
   */
  addXP(amount: number): boolean {
    this.data.xp += amount;
    let leveledUp = false;

    // Check for level-ups (can level up multiple times)
    while (this.data.xpLevel < DEFAULT_XP_LEVELS.length) {
      const required = this.getXPForNextLevel();
      if (required <= 0) break; // max level
      if (this.data.xp >= required) {
        this.data.xp -= required;
        this.data.xpLevel += 1;
        leveledUp = true;
      } else {
        break;
      }
    }

    this.save();
    return leveledUp;
  }

  addTrophies(amount: number): void {
    this.data.trophies += amount;
    this.save();
  }

  loseTrophies(amount: number): void {
    const floor = this.getArenaFloor();
    this.data.trophies = Math.max(floor, this.data.trophies - amount);
    this.save();
  }

  completeLevel(level: number): void {
    if (level > this.data.highestLevelCompleted) {
      this.data.highestLevelCompleted = level;
    }
    this.data.currentLevel = level + 1;

    // Check onboarding completion (level 5)
    if (level >= 5 && !this.data.onboardingComplete) {
      this.data.onboardingComplete = true;
    }

    this.save();
  }

  /**
   * Use a life. Returns false if no lives available.
   */
  useLife(): boolean {
    this.updateLives(); // apply any pending regen first
    if (this.data.lives <= 0) return false;

    // If going from max to below max, start regen timer
    if (this.data.lives === this.data.maxLives) {
      this.data.lastLifeRegenTimestamp = Date.now();
    }

    this.data.lives -= 1;
    this.save();
    return true;
  }

  /**
   * Check passive life regeneration and grant any pending lives.
   */
  updateLives(): void {
    if (this.data.lives >= this.data.maxLives) {
      this.data.lastLifeRegenTimestamp = Date.now();
      return;
    }

    const now = Date.now();
    const elapsed = now - this.data.lastLifeRegenTimestamp;
    const livesToRegen = Math.floor(elapsed / LIFE_REGEN_MS);

    if (livesToRegen > 0) {
      this.data.lives = Math.min(this.data.maxLives, this.data.lives + livesToRegen);
      this.data.lastLifeRegenTimestamp += livesToRegen * LIFE_REGEN_MS;

      // If we hit max, reset the timestamp
      if (this.data.lives >= this.data.maxLives) {
        this.data.lastLifeRegenTimestamp = now;
      }

      this.save();
    }
  }

  incrementWinStreak(): void {
    this.data.winStreak += 1;
    this.save();
  }

  resetWinStreak(): void {
    this.data.winStreak = 0;
    this.save();
  }

  // --- Arena system ---

  /**
   * Get the current arena config based on trophy count.
   */
  getCurrentArena(): ArenaConfig {
    const arenas = DEFAULT_ARENAS;
    let current = arenas[0];
    for (const a of arenas) {
      if (this.data.trophies >= a.trophies) {
        current = a;
      } else {
        break;
      }
    }
    return current;
  }

  /**
   * Get the arena floor — trophies can never drop below the highest
   * arena threshold the player has reached.
   */
  getArenaFloor(): number {
    const arenas = DEFAULT_ARENAS;
    let floor = 0;
    for (const a of arenas) {
      if (this.data.trophies >= a.trophies) {
        floor = a.trophies;
      } else {
        break;
      }
    }
    return floor;
  }

  /**
   * Get the trophy bracket for the current trophy count.
   */
  getTrophyBracket(): TrophyBracket {
    const brackets = DEFAULT_TROPHY_BRACKETS;
    for (const b of brackets) {
      if (this.data.trophies >= b.minTrophies && this.data.trophies <= b.maxTrophies) {
        return b;
      }
    }
    return brackets[brackets.length - 1];
  }

  /**
   * Calculate the win streak trophy bonus.
   */
  getWinStreakBonus(): number {
    if (this.data.winStreak < WIN_STREAK_MIN_WINS) return 0;
    const streakBonusWins = this.data.winStreak - WIN_STREAK_MIN_WINS + 1;
    return Math.min(streakBonusWins * WIN_STREAK_BONUS, WIN_STREAK_BONUS_CAP);
  }

  // --- Computed ---

  /**
   * XP required to reach the next level.
   * Returns 0 if at max level.
   */
  getXPForNextLevel(): number {
    const levels = DEFAULT_XP_LEVELS;
    const idx = this.data.xpLevel - 1; // 0-based
    if (idx >= levels.length) return 0;
    return levels[idx].xpRequired;
  }

  /**
   * Milliseconds until the next life regenerates.
   * Returns 0 if lives are full.
   */
  getLivesRegenTimeRemaining(): number {
    if (this.data.lives >= this.data.maxLives) return 0;
    const elapsed = Date.now() - this.data.lastLifeRegenTimestamp;
    const remaining = LIFE_REGEN_MS - elapsed;
    return Math.max(0, remaining);
  }

  /**
   * Check if a given ability is unlocked.
   */
  isAbilityUnlocked(abilityId: string): boolean {
    return this.data.abilitiesUnlocked.includes(abilityId);
  }

  /**
   * Unlock an ability by ID.
   */
  unlockAbility(abilityId: string): void {
    if (!this.data.abilitiesUnlocked.includes(abilityId)) {
      this.data.abilitiesUnlocked.push(abilityId);
      this.save();
    }
  }

  // --- Ability shards & charges ---

  getShards(abilityId: string): number {
    return this.data.abilityShards[abilityId] ?? 0;
  }

  addShards(abilityId: string, amount: number): void {
    this.data.abilityShards[abilityId] = (this.data.abilityShards[abilityId] ?? 0) + amount;
    this.save();
  }

  getCharges(abilityId: string): number {
    return this.data.abilityCharges[abilityId] ?? 0;
  }

  addCharges(abilityId: string, amount: number, maxCharges?: number): void {
    const current = this.data.abilityCharges[abilityId] ?? 0;
    const cap = maxCharges ?? Infinity;
    this.data.abilityCharges[abilityId] = Math.min(cap, current + amount);
    this.save();
  }

  useCharge(abilityId: string): boolean {
    const current = this.data.abilityCharges[abilityId] ?? 0;
    if (current <= 0) return false;
    this.data.abilityCharges[abilityId] = current - 1;
    this.save();
    return true;
  }

  equipAbility(abilityId: string, maxSlots: number): boolean {
    if (this.data.equippedAbilities.includes(abilityId)) return true;
    if (this.data.equippedAbilities.length >= maxSlots) return false;
    this.data.equippedAbilities.push(abilityId);
    this.save();
    return true;
  }

  unequipAbility(abilityId: string): void {
    this.data.equippedAbilities = this.data.equippedAbilities.filter(a => a !== abilityId);
    this.save();
  }

  setEquippedAbilities(abilities: string[]): void {
    this.data.equippedAbilities = abilities;
    this.save();
  }

  spendCoins(amount: number): boolean {
    if (this.data.coins < amount) return false;
    this.data.coins -= amount;
    this.save();
    return true;
  }

  // --- Daily tasks access (mutated by DailyTaskManager) ---

  setDailyTasks(tasks: ActiveTask[]): void {
    this.data.dailyTasks = tasks;
    this.save();
  }

  setDailyTasksLastReset(isoDate: string): void {
    this.data.dailyTasksLastReset = isoDate;
    this.save();
  }

  setDailyChestClaimed(claimed: boolean): void {
    this.data.dailyChestClaimed = claimed;
    this.save();
  }

  // --- Chest access (mutated by ChestManager) ---

  setChestSlots(slots: ChestSlot[]): void {
    this.data.chestSlots = slots;
    this.save();
  }

  setOverflowChest(chest: ChestSlot | null): void {
    this.data.overflowChest = chest;
    this.save();
  }

  setBronzeStreak(count: number): void {
    this.data.bronzeStreak = count;
    this.save();
  }

  setChestsSinceGold(count: number): void {
    this.data.chestsSinceGold = count;
    this.save();
  }

  // --- Static reward constants ---

  static get COINS_PER_WIN(): number { return COINS_PER_WIN; }
  static get XP_PER_WIN(): number { return XP_PER_WIN; }
  static get XP_PER_LOSS(): number { return XP_PER_LOSS; }

  // --- Save / Load / Reset ---

  save(): void {
    saveManager.save(this.data);
  }

  load(): void {
    const saved = saveManager.load();
    if (saved) {
      this.data = saved;
      // Apply any pending life regen
      this.updateLives();
    }
  }

  reset(): void {
    saveManager.clear();
    this.data = createDefaultState();
  }
}

export const playerState = new PlayerState();
export default playerState;
